/**
 * "Where can the voucher be used?" for an Event in a Box event CTA (migration 2026-11-02-1540).
 * Renders nothing when the server does not offer the choice (classes / tables / seminars, no-voucher states, CTAs
 * without a voucher), except a short note when a statewide / nationwide scope is saved but no longer in effect.
 * Changing it never changes the link, QR code or flyer: a printed QR keeps working.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../ui/Toast";
import {
  getVoucherScopeOptions,
  setVoucherScope,
  voucherScopeChoices,
  type VoucherScope,
} from "../../services/eventCTAVoucherScope.service";

interface Props {
  ctaId: string;
  /** Refetch key parts: the options change when the CTA's voucher, state or expiry change. */
  rewardType?: string | null;
  voucherEndDate?: string | null;
  expiresAtEventEnd?: boolean | null;
  eventState?: string | null;
  eventType?: string | null;
  venueName?: string | null;
}

const CTAVoucherScopeSelector: React.FC<Props> = ({
  ctaId,
  rewardType,
  voucherEndDate,
  expiresAtEventEnd,
  eventState,
  eventType,
  venueName,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = [
    "event-cta-voucher-scope",
    ctaId,
    rewardType,
    voucherEndDate,
    expiresAtEventEnd,
    eventState,
  ];
  const { data, isPending, isError } = useQuery({
    queryKey,
    queryFn: () => getVoucherScopeOptions(ctaId),
    staleTime: 30_000,
  });
  const save = useMutation({
    mutationFn: (scope: VoucherScope) => setVoucherScope(ctaId, scope),
    onSuccess: () => {
      showToast("success", "Saved where the voucher can be used.");
      queryClient.invalidateQueries({
        queryKey: ["event-cta-voucher-scope", ctaId],
      });
    },
    onError: (e: unknown) =>
      showToast("error", e instanceof Error ? e.message : String(e)),
  });

  if (isPending || isError || !data) {
    return null;
  }
  if (!data.offered) {
    if (data.scope && data.scope !== "event") {
      return (
        <p
          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid="cta-voucher-scope-inactive"
        >
          This voucher is set to be good{" "}
          {data.scope === "state" ? "statewide" : "nationwide"}, but that is not
          in effect: {data.words} New vouchers are good at this event only.
        </p>
      );
    }
    return null;
  }

  const current: VoucherScope = data.scope ?? "event";
  const disabled = !data.can_change || save.isPending;
  return (
    <fieldset
      className="border border-gray-200 rounded-lg p-4 space-y-2"
      data-testid="cta-voucher-scope"
    >
      <legend className="text-sm font-medium text-gray-700 px-1">
        Where can the voucher be used?
      </legend>
      {voucherScopeChoices(
        data.state_name || data.state || "the state",
        eventType,
        venueName,
      ).map((c) => (
        <label
          key={c.value}
          className={`flex items-start gap-2 text-sm ${disabled ? "opacity-75" : "cursor-pointer"}`}
        >
          <input
            type="radio"
            name={`voucher-scope-${ctaId}`}
            value={c.value}
            checked={current === c.value}
            disabled={disabled}
            onChange={() => save.mutate(c.value)}
            className="mt-1 border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            <span className="font-medium text-gray-800">{c.label}</span>
            {c.value === "event" && (
              <span className="text-gray-500"> (default)</span>
            )}
            <span className="block text-xs text-gray-500">{c.help}</span>
          </span>
        </label>
      ))}
      <p className="text-xs text-gray-500">
        The amount is the same for every choice. Changing this does not change
        the link, QR code or flyer; vouchers already issued keep where they were
        good.
        {!data.can_change &&
          " You can see this choice but not change it on this event."}
      </p>
    </fieldset>
  );
};

export default CTAVoucherScopeSelector;
